/**
 * ThreadPanel — Pannello laterale per thread di risposte (stile Slack)
 * Notion-style design
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Loader2, Paperclip } from 'lucide-react';
import { useTeamChatStore, TeamChatMessage, MessageAttachment } from '../../lib/stores/teamChatStore';
import { MessageContent } from './MessageContent';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth } from '../../lib/auth';
import { uploadChatFile } from '../../lib/services/teamChatUploadService';
import toast from 'react-hot-toast';

interface ThreadPanelProps {
  chatId: string;
  rootMessage: TeamChatMessage;
  onClose: () => void;
  organizationUsers: { id: string; full_name: string; profile_image_url?: string | null }[];
}

export function ThreadPanel({ chatId, rootMessage, onClose, organizationUsers }: ThreadPanelProps) {
  const { fetchThreadMessages, sendThreadMessage } = useTeamChatStore();
  const { userProfile } = useAuth();
  const [replies, setReplies] = useState<TeamChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const repliesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadReplies = useCallback(async () => {
    setLoading(true);
    try {
      const msgs = await fetchThreadMessages(chatId, rootMessage.id);
      setReplies(msgs);
    } finally {
      setLoading(false);
    }
  }, [chatId, rootMessage.id, fetchThreadMessages]);

  useEffect(() => {
    loadReplies();
  }, [loadReplies]);

  useEffect(() => {
    repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      await sendThreadMessage(chatId, rootMessage.id, input.trim());
      setInput('');
      // Ricarica i reply
      const msgs = await fetchThreadMessages(chatId, rootMessage.id);
      setReplies(msgs);
    } catch {
      toast.error('Errore nell\'invio della risposta');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderAvatar = (msg: TeamChatMessage) => {
    const sender = msg.sender;
    if (sender?.profile_image_url) {
      return <img src={sender.profile_image_url} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />;
    }
    return (
      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0" style={{ backgroundColor: 'rgba(55,53,47,0.12)', color: '#37352F' }}>
        {sender?.full_name?.[0]?.toUpperCase() || '?'}
      </div>
    );
  };

  const renderMessage = (msg: TeamChatMessage, isRoot = false) => {
    const isOwn = msg.sender_id === userProfile?.id;
    return (
      <div key={msg.id} className={`flex gap-2 px-3 py-2 ${isRoot ? '' : 'hover:bg-[rgba(55,53,47,0.03)]'}`}>
        {renderAvatar(msg)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold" style={{ color: '#37352F' }}>
              {msg.sender?.full_name || 'Sistema'}
            </span>
            <span className="text-[10px]" style={{ color: '#9B9A97' }}>
              {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true, locale: it })}
            </span>
          </div>
          <div className="mt-0.5">
            <MessageContent
              message={msg}
              isOwn={isOwn}
              organizationUsers={organizationUsers}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="flex flex-col h-full bg-white"
      style={{ width: 350, borderLeft: '1px solid rgba(55,53,47,0.09)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 flex-shrink-0" style={{ borderBottom: '1px solid rgba(55,53,47,0.09)' }}>
        <div className="flex items-center gap-1.5">
          <span className="text-[13px] font-semibold" style={{ color: '#37352F' }}>Thread</span>
          {replies.length > 0 && (
            <span className="text-[11px] px-1.5 rounded-full" style={{ backgroundColor: 'rgba(35,131,226,0.1)', color: '#2383E2' }}>
              {replies.length} {replies.length === 1 ? 'risposta' : 'risposte'}
            </span>
          )}
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)] transition-colors">
          <X className="w-3.5 h-3.5" style={{ color: '#787774' }} strokeWidth={1.5} />
        </button>
      </div>

      {/* Root message */}
      <div style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
        {renderMessage(rootMessage, true)}
      </div>

      {/* Replies */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#2383E2' }} />
          </div>
        ) : replies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <p className="text-[12px]" style={{ color: '#9B9A97' }}>Nessuna risposta ancora</p>
            <p className="text-[11px] mt-1" style={{ color: '#D3D1CB' }}>Scrivi la prima risposta a questo messaggio</p>
          </div>
        ) : (
          <div>
            <div className="px-3 py-1.5">
              <span className="text-[10px] font-medium" style={{ color: '#9B9A97' }}>
                {replies.length} {replies.length === 1 ? 'risposta' : 'risposte'}
              </span>
            </div>
            {replies.map(r => renderMessage(r))}
          </div>
        )}
        <div ref={repliesEndRef} />
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0" style={{ borderTop: '1px solid rgba(55,53,47,0.09)' }}>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Rispondi nel thread..."
          className="flex-1 px-3 py-2 text-[13px] rounded-lg outline-none"
          style={{
            backgroundColor: 'rgba(55,53,47,0.04)',
            color: '#37352F',
            border: '1px solid rgba(55,53,47,0.16)',
          }}
          onFocus={e => {
            e.target.style.borderColor = 'rgba(35,131,226,0.5)';
            e.target.style.boxShadow = '0 0 0 2px rgba(35,131,226,0.2)';
          }}
          onBlur={e => {
            e.target.style.borderColor = 'rgba(55,53,47,0.16)';
            e.target.style.boxShadow = 'none';
          }}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || sending}
          className="p-2 rounded-full text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: '#2383E2' }}
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
        </button>
      </div>
    </div>
  );
}
